import React, { createContext, useContext, useState, useEffect } from 'react';
import { translations } from './translations';
import { translationsES } from './translationsES';
import { translationsAR } from './translationsAR';
import { translationsZH } from './translationsZH';

// Merge all translations
const allTranslations = {
  fr: translations.fr,
  en: translations.en,
  es: translationsES,
  ar: translationsAR,
  zh: translationsZH
};

const LanguageContext = createContext();

// Order: English, French, Spanish, Arabic, Chinese (as per user request)
export const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'fr', name: 'Français' },
  { code: 'es', name: 'Español' },
  { code: 'ar', name: 'العربية', rtl: true },
  { code: 'zh', name: '中文' }
];

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageRaw] = useState(() => {
    const saved = localStorage.getItem('hp-language');
    // Check if saved language is valid
    if (saved && ['en', 'fr', 'es', 'ar', 'zh'].includes(saved)) {
      return saved;
    }
    // Try to detect browser language
    const browserLang = navigator.language.split('-')[0];
    if (['en', 'fr', 'es', 'ar', 'zh'].includes(browserLang)) {
      return browserLang;
    }
    return 'fr';
  });

  // Wrap setLanguage to flag manual override (used by MarketContext geo-detect)
  const setLanguage = (newLang) => {
    setLanguageRaw(newLang);
    localStorage.setItem('hp-language-manual', '1');
  };

  useEffect(() => {
    localStorage.setItem('hp-language', language);
    document.documentElement.lang = language;
  }, [language]);

  // Listen for geo-detect language event (fires only if user hasn't manually chosen)
  useEffect(() => {
    const handler = (e) => {
      const detected = e.detail;
      if (detected && ['en', 'fr', 'es', 'ar', 'zh'].includes(detected)) {
        setLanguageRaw(detected);
      }
    };
    window.addEventListener('plexterra:geo-language', handler);
    return () => window.removeEventListener('plexterra:geo-language', handler);
  }, []);

  const t = (key) => {
    const keys = key.split('.');
    let value = allTranslations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    // Fallback to French if translation not found
    if (value === undefined) {
      value = allTranslations['fr'];
      for (const k of keys) {
        value = value?.[k];
      }
    }
    return value || key;
  };

  const cycleLanguage = () => {
    const currentIndex = LANGUAGES.findIndex(l => l.code === language);
    const nextIndex = (currentIndex + 1) % LANGUAGES.length;
    setLanguage(LANGUAGES[nextIndex].code);
  };

  return (
    <LanguageContext.Provider value={{ 
      language, 
      setLanguage, 
      cycleLanguage, 
      t,
      languages: LANGUAGES,
      currentLanguage: LANGUAGES.find(l => l.code === language)
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
